import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  Query,
  QueryConstraint,
  runTransaction,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
  setDoc,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../../../shared/utils/firebase';
import {
  CreateLeaveRequestInput,
  LeaveAllocationFilter,
  LeaveAllocationRecord,
  LeaveAllocationUpsertInput,
  LeaveRequestFilter,
  LeaveRequestHistoryEntry,
  LeaveRequestRecord,
  LeaveRequestStatus,
  UpdateLeaveStatusInput,
} from '../types';
import { calculateAllocationTotals } from '../utils/leaveRequestStatusRules';

const LEAVE_REQUESTS_COLLECTION = 'leaveRequests';
const LEAVE_ALLOCATIONS_COLLECTION = 'leaveAllocations';
const STAFF_LOCATIONS_COLLECTION = 'staffLocations';

const leaveRequestsCollection = () => collection(db, LEAVE_REQUESTS_COLLECTION);
const leaveAllocationsCollection = () => collection(db, LEAVE_ALLOCATIONS_COLLECTION);
const staffLocationsCollection = () => collection(db, STAFF_LOCATIONS_COLLECTION);

const toTimestamp = (date: Date): Timestamp => Timestamp.fromDate(date);

const calculateTotalLeaveDays = (startDate: Date, endDate: Date): number => {
  const msPerDay = 24 * 60 * 60 * 1000;
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  const diff = Math.round((end.getTime() - start.getTime()) / msPerDay);
  return diff >= 0 ? diff + 1 : 1;
};

/** Thrown when approving or denying a request that is no longer waiting for a decision. */
export class LeaveRequestAlreadyDecidedError extends Error {
	constructor(public readonly currentStatus: LeaveRequestStatus) {
		super(`Leave request is already ${String(currentStatus).toLowerCase()}`);
		this.name = 'LeaveRequestAlreadyDecidedError';
	}
}

const AWAITING_DECISION: LeaveRequestStatus[] = ['PENDING', 'CHANGE_PENDING'];

const isDecisionStatus = (status: LeaveRequestStatus) => status === 'APPROVED' || status === 'DENIED';

/**
 * Checks a decision against the request as it stands now. A decision only
 * applies to a request still waiting for one, so two managers deciding at once
 * can't overwrite each other or notify the requester twice. Returns true when
 * this person's identical decision is already recorded (a retry after a
 * failure that had in fact committed): the write is skipped and treated as done.
 */
const decisionAlreadyRecorded = (
	nextStatus: LeaveRequestStatus,
	deciderUid: string,
	current: DocumentData | undefined
): boolean => {
	if (!isDecisionStatus(nextStatus)) return false;
	const history: LeaveRequestHistoryEntry[] = Array.isArray(current?.history) ? current?.history ?? [] : [];
	const latest = history[history.length - 1] as LeaveRequestHistoryEntry | undefined;
	if (current?.status === nextStatus && latest?.status === nextStatus && latest?.changedBy?.uid === deciderUid) {
		return true;
	}
	if (!AWAITING_DECISION.includes(current?.status)) {
		throw new LeaveRequestAlreadyDecidedError(current?.status);
	}
	return false;
};

export const retryWithBackoff = async <T>(
	fn: () => Promise<T>,
	maxAttempts = 3,
	delayMs = 250
): Promise<T> => {
	let attempt = 0;
	while (attempt < maxAttempts) {
		try {
			if (attempt > 0) {
				console.info('[leave] retrying operation', { attempt });
			}
			return await fn();
		} catch (error) {
			// Not transient: retrying would hit the same decided request again.
			if (error instanceof LeaveRequestAlreadyDecidedError) throw error;
			console.error('[leave] operation failed', { attempt, error });
			attempt += 1;
			if (attempt >= maxAttempts) throw error;
			await new Promise((resolve) => setTimeout(resolve, delayMs * attempt));
		}
	}
	throw new Error('Retry failed');
};

export const getPrimaryLocationForCoach = async (
  coachId?: string | number | null
): Promise<string | null> => {
  if (!coachId && coachId !== 0) {
    return null;
  }

  const coachIdStr = typeof coachId === 'string' ? coachId : coachId?.toString();
  if (!coachIdStr) return null;

  const locationDocRef = doc(staffLocationsCollection(), coachIdStr);
  const snapshot = await getDoc(locationDocRef);

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data();
  return (data?.primary_location as string | null) || null;
};

const buildHistoryEntry = (
  status: LeaveRequestStatus,
  changedBy: UpdateLeaveStatusInput['changedBy'],
  note?: string,
  changedAt: Timestamp = Timestamp.now()
): LeaveRequestHistoryEntry => {
  const entry: LeaveRequestHistoryEntry = {
    status,
    changedBy,
    changedAt,
  };

  if (note !== undefined && note !== null && note !== '') {
    entry.note = note;
  }

  return entry;
};

export const buildLeaveRequestsQuery = (filter: LeaveRequestFilter): Query => {
  const constraints: QueryConstraint[] = [orderBy('period.startDate', 'asc')];

  if (filter.status) {
    constraints.push(where('status', '==', filter.status));
  }

  if (filter.year) {
    constraints.push(where('period.year', '==', filter.year));
  }

  if (filter.role === 'USER') {
    if (!filter.uid) {
      throw new Error('User leave request query requires a uid');
    }
    constraints.push(where('visibility.staffUid', '==', filter.uid));
  }

  if (filter.role === 'MANAGER') {
    if (!filter.managerLocations || filter.managerLocations.length === 0) {
      throw new Error('Manager leave request query requires managerLocations');
    }
    constraints.push(where('visibility.managerLocations', 'array-contains-any', filter.managerLocations));
  }

  return query(leaveRequestsCollection(), ...constraints);
};

/**
 * Every request still awaiting a decision, across all locations: the admin
 * queue. Equality-only filters, so Firestore serves it from single-field
 * indexes. Managers use buildLeaveRequestsQuery scoped to their location.
 */
export const buildPendingLeaveRequestsQuery = (): Query =>
  query(leaveRequestsCollection(), where('status', 'in', ['PENDING', 'CHANGE_PENDING']));

export const buildLeaveAllocationsQuery = (filter: LeaveAllocationFilter): Query => {
  const constraints: QueryConstraint[] = [
    where('year', '==', filter.year),
    orderBy('staffName', 'asc')
  ];

  if (filter.role === 'USER') {
    if (!filter.uid) {
      throw new Error('User leave allocation query requires a uid');
    }
    constraints.push(where('staffUid', '==', filter.uid));
  }

  if (filter.role === 'MANAGER') {
    if (!filter.managerLocations || filter.managerLocations.length === 0) {
      throw new Error('Manager leave allocation query requires managerLocations');
    }
    constraints.push(where('location', 'in', filter.managerLocations));
  }

  return query(leaveAllocationsCollection(), ...constraints);
};

const composeLeaveRequestRecord = (id: string, data: DocumentData): LeaveRequestRecord => ({
  id,
  status: data.status,
  reason: data.reason,
  comments: data.comments || {},
  staff: data.staff,
  manager: data.manager,
  visibility: data.visibility,
  period: data.period,
  history: data.history || [],
  change: data.change,
  timeline: data.timeline,
} as LeaveRequestRecord);

export const mapLeaveRequestSnapshot = (
  snapshot: QueryDocumentSnapshot<DocumentData>
): LeaveRequestRecord => composeLeaveRequestRecord(snapshot.id, snapshot.data());

export const fetchLeaveRequests = async (filter: LeaveRequestFilter): Promise<LeaveRequestRecord[]> => {
  const builtQuery = buildLeaveRequestsQuery(filter);
  const snapshot = await getDocs(builtQuery);
  return snapshot.docs.map((docSnapshot) => mapLeaveRequestSnapshot(docSnapshot));
};

export const createLeaveRequest = async (input: CreateLeaveRequestInput): Promise<string> => {
  const { staff, manager, period, reason, staffComment, originalRequestId } = input;

  const startDate = toTimestamp(period.startDate);
  const endDate = toTimestamp(period.endDate);
  const totalDays = calculateTotalLeaveDays(period.startDate, period.endDate);
  const year = period.startDate.getFullYear();

  const now = Timestamp.now();

  const managerLocations = new Set<string>();
  (manager?.locations || []).forEach((location) => {
    if (location) {
      managerLocations.add(location);
    }
  });
  if (staff.location) {
    managerLocations.add(staff.location);
  }

  const newRequest = {
    status: originalRequestId ? ('CHANGE_PENDING' as LeaveRequestStatus) : ('PENDING' as LeaveRequestStatus),
    reason,
    comments: {
      staff: staffComment || null,
      manager: null,
    },
    staff: {
      uid: staff.uid,
      displayName: staff.displayName,
      coachId: staff.coachId || null,
      email: staff.email || null,
    },
    manager: manager || null,
    visibility: {
      staffUid: staff.uid,
      managerUid: manager?.uid || null,
      managerLocations: Array.from(managerLocations),
      staffLocation: staff.location || null,
    },
    period: {
      startDate,
      endDate,
      totalDays,
      year,
    },
    history: [
      buildHistoryEntry(originalRequestId ? 'CHANGE_PENDING' : 'PENDING', {
        uid: staff.uid,
        displayName: staff.displayName,
        role: 'USER',
      }),
    ],
    change: {
      originalRequestId: originalRequestId || null,
      isChangeRequest: Boolean(originalRequestId),
    },
    timeline: {
      createdAt: now,
      updatedAt: now,
    },
  };

  const docRef = await addDoc(leaveRequestsCollection(), newRequest);
  return docRef.id;
};

export const requestLeaveChange = async (requestId: string, input: CreateLeaveRequestInput) => {
	console.info('[leave] creating change request', {
		originalRequestId: requestId,
		staffUid: input.staff.uid,
	});

	return retryWithBackoff(async () => {
		const originalRef = doc(db, LEAVE_REQUESTS_COLLECTION, requestId);
		const changeRequestRef = doc(leaveRequestsCollection());
		const now = Timestamp.now();

		return runTransaction(db, async (transaction) => {
			const originalSnapshot = await transaction.get(originalRef);
			if (!originalSnapshot.exists()) {
				throw new Error('Original leave request not found');
			}

			const originalData = originalSnapshot.data();
			const originalStatus = (originalData?.status as LeaveRequestStatus) || 'APPROVED';
			const originalYear = originalData?.period?.year ?? input.period.startDate.getFullYear();

			const startDate = toTimestamp(input.period.startDate);
			const endDate = toTimestamp(input.period.endDate);
			const totalDays = calculateTotalLeaveDays(input.period.startDate, input.period.endDate);
			const year = input.period.startDate.getFullYear();

			const managerLocations = new Set<string>();
			(input.manager?.locations || []).forEach((location) => {
				if (location) {
					managerLocations.add(location);
				}
			});
			if (input.staff.location) {
				managerLocations.add(input.staff.location);
			}

			const changedBy = {
				uid: input.staff.uid,
				displayName: input.staff.displayName,
				role: 'USER' as const,
			};

			// Preserve original status/year for later restoration and allocation recompute.
			const changeRequest = {
				status: 'CHANGE_PENDING' as LeaveRequestStatus,
				reason: input.reason,
				comments: {
					staff: input.staffComment || null,
					manager: null,
				},
				staff: {
					uid: input.staff.uid,
					displayName: input.staff.displayName,
					coachId: input.staff.coachId || null,
					email: input.staff.email || null,
				},
				manager: input.manager || null,
				visibility: {
					staffUid: input.staff.uid,
					managerUid: input.manager?.uid || null,
					managerLocations: Array.from(managerLocations),
					staffLocation: input.staff.location || null,
				},
				period: {
					startDate,
					endDate,
					totalDays,
					year,
				},
				history: [
					buildHistoryEntry('CHANGE_PENDING', changedBy),
				],
				change: {
					originalRequestId: requestId,
					originalStatus,
					originalYear,
					isChangeRequest: true,
				},
				timeline: {
					createdAt: now,
					updatedAt: now,
				},
			};

			// Cancel the original request immediately to avoid double-counting.
			transaction.update(originalRef, {
				status: 'CANCELLED',
				history: arrayUnion(buildHistoryEntry('CANCELLED', changedBy, 'Change request submitted')),
				'timeline.updatedAt': now,
			});

			transaction.set(changeRequestRef, changeRequest);

			console.info('[leave] change request staged', {
				changeRequestId: changeRequestRef.id,
				originalRequestId: requestId,
			});

			return changeRequestRef.id;
		});
	});
};

export interface LeaveStatusUpdateOutcome {
	record: LeaveRequestRecord;
	/**
	 * False when this person's identical decision was already recorded before
	 * this call (a second submit of the same decision), so nothing was written
	 * and the requester was told the first time.
	 */
	written: boolean;
}

export const updateLeaveRequestStatusWithOutcome = async (
  input: UpdateLeaveStatusInput
): Promise<LeaveStatusUpdateOutcome> => {
	const { requestId, status, changedBy, note, managerComment } = input;
	const requestRef = doc(db, LEAVE_REQUESTS_COLLECTION, requestId);
	const requestSnapshot = await getDoc(requestRef);

	if (!requestSnapshot.exists()) {
		throw new Error('Leave request not found');
	}

	const existingData = requestSnapshot.data();
	const alreadyRecorded = decisionAlreadyRecorded(status, changedBy.uid, existingData);
	const isChangeRequest = Boolean(existingData?.change?.originalRequestId);
	const originalRequestId = existingData?.change?.originalRequestId as string | undefined;
	const originalStatus = (existingData?.change?.originalStatus as LeaveRequestStatus) || 'APPROVED';

	console.info('[leave] updating request status', {
		requestId,
		status,
		isChangeRequest,
	});

	const historyEntry = buildHistoryEntry(status, changedBy, note);
	const updatedAt = Timestamp.now();

	if (alreadyRecorded) {
		console.info('[leave] decision already recorded by this user; not writing it again', { requestId, status });
	} else if (isChangeRequest && (status === 'DENIED' || status === 'CANCELLED') && originalRequestId) {
		// Change requests that are denied/cancelled restore the original request status.
		const originalRef = doc(db, LEAVE_REQUESTS_COLLECTION, originalRequestId);
		const restoreNote =
			status === 'DENIED'
				? 'Change request denied; original restored'
				: 'Change request cancelled; original restored';
		const restoreEntry = buildHistoryEntry(originalStatus, changedBy, restoreNote);

		await retryWithBackoff(() =>
			runTransaction(db, async (transaction) => {
				const current = await transaction.get(requestRef);
				if (decisionAlreadyRecorded(status, changedBy.uid, current.data())) return;

				transaction.update(requestRef, {
					status,
					'comments.manager': managerComment ?? null,
					history: arrayUnion(historyEntry),
					'timeline.updatedAt': updatedAt,
				});

				transaction.update(originalRef, {
					status: originalStatus,
					history: arrayUnion(restoreEntry),
					'timeline.updatedAt': updatedAt,
				});

				console.info('[leave] restored original request', {
					originalRequestId,
					restoredStatus: originalStatus,
				});
			})
		);
	} else if (isDecisionStatus(status)) {
		await retryWithBackoff(() =>
			runTransaction(db, async (transaction) => {
				const current = await transaction.get(requestRef);
				if (decisionAlreadyRecorded(status, changedBy.uid, current.data())) return;

				transaction.update(requestRef, {
					status,
					'comments.manager': managerComment ?? null,
					history: arrayUnion(historyEntry),
					'timeline.updatedAt': updatedAt,
				});
			})
		);
	} else {
		// Cancellations need no guard, and a plain update still queues while offline.
		await retryWithBackoff(() =>
			updateDoc(requestRef, {
				status,
				'comments.manager': managerComment ?? null,
				history: arrayUnion(historyEntry),
				'timeline.updatedAt': updatedAt,
			})
		);
	}

	const updatedSnapshot = await getDoc(requestRef);
	const data = updatedSnapshot.data();
	if (!data) {
		throw new Error('Failed to load updated leave request');
	}

	// A hit on the check inside the transaction is taken to be this call's own
	// earlier attempt (retried after a failure that had in fact committed), so it
	// still counts as written by this call.
	return { record: composeLeaveRequestRecord(requestId, data), written: !alreadyRecorded };
};

export const updateLeaveRequestStatus = async (
  input: UpdateLeaveStatusInput
): Promise<LeaveRequestRecord> => (await updateLeaveRequestStatusWithOutcome(input)).record;

export const cancelLeaveRequest = async (
  requestId: string,
  cancelledBy: UpdateLeaveStatusInput['changedBy'],
  note?: string
): Promise<LeaveRequestRecord> => {
  return updateLeaveRequestStatus({
    requestId,
    status: 'CANCELLED',
    changedBy: cancelledBy,
    note,
  });
};

export const mapLeaveAllocationSnapshot = (
  snapshot: QueryDocumentSnapshot<DocumentData>
): LeaveAllocationRecord => {
  const data = snapshot.data();
  return {
    id: snapshot.id,
    staffUid: data.staffUid,
    staffName: data.staffName,
    coachId: data.coachId || null,
    location: data.location || null,
    year: data.year,
    allowanceDays: data.allowanceDays,
    approvedDays: data.approvedDays,
    pendingDays: data.pendingDays,
    deniedDays: data.deniedDays,
    updatedAt: data.updatedAt,
  } as LeaveAllocationRecord;
};

export const upsertLeaveAllocation = async (input: LeaveAllocationUpsertInput) => {
  const { staffUid, year } = input;
  const allocationId = `${staffUid}_${year}`;
  const allocationRef = doc(db, LEAVE_ALLOCATIONS_COLLECTION, allocationId);

  const payload = {
    staffUid,
    staffName: input.staffName,
    coachId: input.coachId || null,
    location: input.location || null,
    year,
    allowanceDays: input.allowanceDays ?? null,
    approvedDays: input.approvedDays,
    pendingDays: input.pendingDays,
    deniedDays: input.deniedDays,
    updatedAt: Timestamp.now(),
  };

  await setDoc(allocationRef, payload, { merge: true });
  return allocationId;
};

export const fetchLeaveAllocations = async (filter: LeaveAllocationFilter): Promise<LeaveAllocationRecord[]> => {
  const allocationsQuery = buildLeaveAllocationsQuery(filter);
  const snapshot = await getDocs(allocationsQuery);
  return snapshot.docs.map((docSnapshot) => mapLeaveAllocationSnapshot(docSnapshot));
};

export const fetchLeaveRequestsForStaff = async (
  staffUid: string,
  year?: number
): Promise<LeaveRequestRecord[]> => {
  const constraints: QueryConstraint[] = [where('visibility.staffUid', '==', staffUid)];
  if (year) {
    constraints.push(where('period.year', '==', year));
  }
  const staffQuery = query(leaveRequestsCollection(), ...constraints);
  const snapshot = await getDocs(staffQuery);
  return snapshot.docs.map((docSnapshot) => mapLeaveRequestSnapshot(docSnapshot));
};

export const recalculateAllocationForRequests = async (
  staffUid: string,
  year: number,
  requests: LeaveRequestRecord[]
) => {
	const { approvedDays, pendingDays, deniedDays } = calculateAllocationTotals(requests, year);

  await upsertLeaveAllocation({
    staffUid,
    staffName: requests[0]?.staff.displayName || 'Unknown',
    coachId: requests[0]?.staff.coachId || null,
    location: requests[0]?.visibility.staffLocation || null,
    year,
    approvedDays,
    pendingDays,
    deniedDays,
  });
};

const summariseRequestsForYear = (requests: LeaveRequestRecord[], year: number) => {
	// Centralize allocation math to prevent change-request double counting.
	return calculateAllocationTotals(requests, year);
};

export const recomputeAllocationSnapshot = async (
  staffUid: string,
  year: number,
  fallback?: { staffName: string; coachId?: string | null; location?: string | null }
) => {
  const requests = await fetchLeaveRequestsForStaff(staffUid, year);
  if (requests.length === 0) {
    if (!fallback) {
      return;
    }
    await upsertLeaveAllocation({
      staffUid,
      staffName: fallback.staffName,
      coachId: fallback.coachId || null,
      location: fallback.location || null,
      year,
      approvedDays: 0,
      pendingDays: 0,
      deniedDays: 0,
    });
    return;
  }

  const { approvedDays, pendingDays, deniedDays } = summariseRequestsForYear(requests, year);

  await upsertLeaveAllocation({
    staffUid,
    staffName: requests[0]?.staff.displayName || fallback?.staffName || 'Unknown',
    coachId: requests[0]?.staff.coachId || fallback?.coachId || null,
    location: requests[0]?.visibility.staffLocation || fallback?.location || null,
    year,
    approvedDays,
    pendingDays,
    deniedDays,
  });
};

export const recalculateAllocationsForYear = async (year: number) => {
  const allRequestsSnapshot = await getDocs(
    query(leaveRequestsCollection(), where('period.year', '==', year))
  );

  const requestsByStaff = new Map<string, LeaveRequestRecord[]>();

  allRequestsSnapshot.forEach((docSnapshot) => {
    const record = mapLeaveRequestSnapshot(docSnapshot);
    const staffRequests = requestsByStaff.get(record.staff.uid) || [];
    staffRequests.push(record);
    requestsByStaff.set(record.staff.uid, staffRequests);
  });

  const batch = writeBatch(db);

  requestsByStaff.forEach((requests, staffUid) => {
    const allocationId = `${staffUid}_${year}`;
    const allocationRef = doc(db, LEAVE_ALLOCATIONS_COLLECTION, allocationId);

		const { approvedDays, pendingDays, deniedDays } = calculateAllocationTotals(requests, year);

    batch.set(
      allocationRef,
      {
        staffUid,
        staffName: requests[0]?.staff.displayName || 'Unknown',
        coachId: requests[0]?.staff.coachId || null,
        location: requests[0]?.visibility.staffLocation || null,
        year,
        allowanceDays: null,
        approvedDays,
        pendingDays,
        deniedDays,
        updatedAt: Timestamp.now(),
      },
      { merge: true }
    );
  });

  await batch.commit();
};

export const cancelChangeRequest = async (
  newRequestId: string,
  originalRequestId: string,
  cancelledBy: UpdateLeaveStatusInput['changedBy'],
  note?: string
) => {
	console.info('[leave] cancelling change request', {
		newRequestId,
		originalRequestId,
	});

	await retryWithBackoff(() =>
		runTransaction(db, async (transaction) => {
			const newRequestRef = doc(db, LEAVE_REQUESTS_COLLECTION, newRequestId);
			const originalRequestRef = doc(db, LEAVE_REQUESTS_COLLECTION, originalRequestId);
			const newRequestSnapshot = await transaction.get(newRequestRef);

			if (!newRequestSnapshot.exists()) {
				throw new Error('Change request not found');
			}

			const newRequestData = newRequestSnapshot.data();
			// Use stored original status when available to avoid unintended approvals.
			const restoreStatus = (newRequestData?.change?.originalStatus as LeaveRequestStatus) || 'APPROVED';
			const updatedAt = Timestamp.now();

			transaction.update(newRequestRef, {
				status: 'CANCELLED',
				history: arrayUnion(buildHistoryEntry('CANCELLED', cancelledBy, note)),
				'timeline.updatedAt': updatedAt,
			});

			transaction.update(originalRequestRef, {
				status: restoreStatus,
				history: arrayUnion(buildHistoryEntry(restoreStatus, cancelledBy, 'Change request cancelled; original restored')),
				'timeline.updatedAt': updatedAt,
			});

			console.info('[leave] change request cancelled and original restored', {
				newRequestId,
				originalRequestId,
				restoreStatus,
			});
		})
	);
};
